import AdminTable from '../../../components/admin/AdminTable';
import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Contact, Plus } from 'lucide-react';
import { fetchPersonal, Personal } from '../../../services/personalService';
import EditPersonnelModal from '../../../components/Personnel/EditPersonnelModal';
import DeletePersonnelModal from '../../../components/Personnel/DeletePersonnelModal';
import AddPersonnelModal from '../../../components/Personnel/AddPersonnelModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const PersonnelPage: React.FC = () => {
    const [personal, setPersonal] = useState<Personal[]>([]);
    const [selectedPerson, setSelectedPerson] = useState<Personal | null>(null);
    const [personToDelete, setPersonToDelete] = useState<Personal | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('personal', userLevelId);

    const loadPersonal = useCallback(async () => {
        if (!perm?.can_read) {
            setPersonal([]);
            return;
        }
        try {
            const datos = await fetchPersonal();
            setPersonal(datos.sort((a, b) => a.id - b.id));
        } catch (error) {
            /* Browser diagnostics omitted. */
            setPersonal([]);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadPersonal();
    }, [loadPersonal]);

    const handleDeleteSuccess = () => {
        setPersonal(prev => prev.filter(p => p.id !== personToDelete?.id));
        setPersonToDelete(null);
    };

    const handleAddPersonnel = (nuevoPersonal: Personal) => {
        setPersonal(prev => {
            const updated = [...prev, nuevoPersonal];
            return updated.sort((a, b) => a.id - b.id);
        });
    };

    // Lógica de paginación
    const totalPages = Math.ceil(personal.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPersonal = personal.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando personal..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Personal</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/personnel" className="text-[#c1348b] hover:text-[#8f1f64]">Personal</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            style={{ minWidth: '170.75px', height: '39px' }}
                            onClick={() => setIsAddOpen(true)}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Personal
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <AddPersonnelModal
                    isOpen={isAddOpen}
                    onClose={() => setIsAddOpen(false)}
                    onAddPersonnel={handleAddPersonnel}
                />

                {selectedPerson && (
                    <EditPersonnelModal
                        isOpen={!!selectedPerson}
                        onClose={() => setSelectedPerson(null)}
                        person={selectedPerson}
                        onUpdate={(updated) => {
                            setPersonal(prev => prev.map(p => p.id === updated.id ? updated : p));
                        }}
                    />
                )}

                {personToDelete && (
                    <DeletePersonnelModal
                        isOpen={!!personToDelete}
                        onClose={() => setPersonToDelete(null)}
                        person={personToDelete}
                        onDeleteSuccess={handleDeleteSuccess}
                    />
                )}

                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre y Apellido</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cargo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {perm.can_read && displayedPersonal.map((persona) => (
                                <tr key={persona.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        {persona.usuario ?
                                            `${persona.usuario.first_name || ''} ${persona.usuario.last_name || ''}`.trim() || 'N/A'
                                            : 'N/A'
                                        }
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{persona.usuario?.email || 'N/A'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{persona.cargo || 'N/A'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500">{persona.telefono || 'N/A'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        {persona.fecha_creacion
                                            ? new Date(persona.fecha_creacion).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })
                                            : 'N/A'}
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex gap-2">
                                            {perm.can_update && (
                                                <button
                                                    onClick={() => setSelectedPerson(persona)}
                                                    className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                            {perm.can_delete && (
                                                <button
                                                    onClick={() => setPersonToDelete(persona)}
                                                    className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {perm.can_read && personal.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Contact className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay Personal registrado
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega nuevo personal para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!perm.can_read && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <span className="text-gray-500">No tienes permiso para ver personal</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {perm.can_read && personal.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={personal.length}
                    />
                )}
            </div>
        </div>
    );
};

export default PersonnelPage;
